package com.essntl.features.organization.ui.organizationdetails.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import com.essntl.features.address.domain.model.AddressModel
import com.essntl.features.address.ui.AddressEvent
import com.essntl.features.address.ui.AddressScreen
import com.essntl.features.address.ui.AddressState
import com.essntl.features.address.ui.AddressValidation
import com.essntl.features.organization.ui.organizationdetails.OrganizationDetailsEvent
import com.essntl.features.organization.ui.organizationdetails.OrganizationDetailsState
import com.essntl.uikit.components.button.outline.EsMediumOutlineButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.dropdown.EsInputDropdownMenu
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.delay

@Composable
fun DetailsScreen(
    state: OrganizationDetailsState,
    onEvent: (OrganizationDetailsEvent) -> Unit,
    addressState: AddressState,
    onAddressEvent: (AddressEvent) -> Unit,
    addressValidation: AddressValidation,
) {
    var organizationSearchText by rememberSaveable { mutableStateOf("") }

    LaunchedEffect(addressState.getAddress.data) {
        addressValidation.addressForm.reset()
        addressValidation.addressForm.line1.value = addressState.getAddress.data?.line1.orEmpty()
        addressValidation.addressForm.city.value = addressState.getAddress.data?.city.orEmpty()
        addressValidation.addressForm.country.value = addressState.getAddress.data?.country.orEmpty()
    }

    LaunchedEffect(state.addAddress) {
        if (!state.addAddress) {
            onAddressEvent(AddressEvent.UpdateAddressFields(AddressModel.empty))
            addressValidation.addressForm.reset()
        }
    }

    LaunchedEffect(organizationSearchText, state.getOrganization.data?.type) {
        val type = state.getOrganization.data?.type ?: return@LaunchedEffect
        delay(500)
        onEvent(OrganizationDetailsEvent.SearchParentOrganization(organizationSearchText, type))
    }

    LaunchedEffect(addressState.addEditAddress.isSuccess) {
        if (addressState.addEditAddress.isSuccess) {

            onEvent(
                OrganizationDetailsEvent.UpdateOrganizationFields(
                    state.organization.value.copy(
                        address = addressState.addEditAddress.data,
                    ),
                ),
            )

            onEvent(OrganizationDetailsEvent.EditOrganization)

            onAddressEvent(AddressEvent.ClearSuccessState)
        }
    }

    LaunchedEffect(state.editOrganization.isSuccess) {
        if (state.editOrganization.isSuccess) onEvent(OrganizationDetailsEvent.ClearSuccessState)
    }

    Row(
        modifier = Modifier
            .fillMaxSize()
            .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(vertical = EsConstants.SectionVerticalPadding),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth(0.5f)
                .padding(
                    horizontal = 2.5f.spacing,
                    vertical = 2.spacing,
                ),
        ) {
            EsInput(
                value = state.organization.value.name,
                onValueChange = {
                    onEvent(
                        OrganizationDetailsEvent.UpdateOrganizationFields(
                            state.organization.value.copy(name = it),
                        ),
                    )
                },
                placeholder = "Enter Name",
                label = "Name",
            )

            Spacer(modifier = Modifier.height(2.spacing))

            EsInput(
                value = state.organization.value.notes.orEmpty(),
                onValueChange = {
                    onEvent(
                        OrganizationDetailsEvent.UpdateOrganizationFields(
                            state.organization.value.copy(notes = it),
                        ),
                    )
                },
                placeholder = "Enter Notes",
                singleLine = false,
                label = "Notes",
            )

            Spacer(modifier = Modifier.height(2.spacing))

            EsInput(
                value = state.organization.value.website.orEmpty(),
                onValueChange = {
                    onEvent(
                        OrganizationDetailsEvent.UpdateOrganizationFields(
                            state.organization.value.copy(website = it),
                        ),
                    )
                },
                placeholder = "Enter Website",
                label = "Website",
            )

            Spacer(modifier = Modifier.height(2.spacing))

            EsInputDropdownMenu(
                items = state.getAllOrganizations.data,
                selectedItem = state.selectedParentOrganization,
                text = { it.name },
                onItemSelected = {
                    onEvent(
                        OrganizationDetailsEvent.UpdateOrganizationFields(
                            state.organization.value.copy(parentId = it.id),
                        ),
                    )
                    onEvent(
                        OrganizationDetailsEvent.UpdateParentOrganization(it),
                    )
                },
                label = "Parent Organization",
                placeholder = "Select One",
                loading = state.getAllOrganizations.isLoading &&
                    state.getAllOrganizations.data.isEmpty(),
                topContent = { fm ->
                    Spacer(Modifier.height(0.5.spacing))

                    EsInput(
                        value = organizationSearchText,
                        onValueChange = {
                            organizationSearchText = it
                        },
                        placeholder = "Search",
                        prefix = { EsMediumIcon(res = EsIcons.Search) },
                        modifier = Modifier.fillMaxWidth(),
                    )

                    Spacer(Modifier.height(1.spacing))
                },
                emptyContent = {
                    Text(
                        text = "No organization found!",
                        style = MaterialTheme.esTypography.p2Regular14,
                    )
                },
            ) {
                if (!state.getAllOrganizations.isLastPage) {
                    Spacer(modifier = Modifier.height(2.spacing))

                    EsMediumOutlineButton(
                        onClick = {
                            onEvent(OrganizationDetailsEvent.LoadParentOrganizations)
                        },
                        text = "Load more",
                        loading = state.getAllOrganizations.isLoading,
                        modifier = Modifier.align(Alignment.CenterHorizontally),
                    )
                }
            }

            Spacer(modifier = Modifier.height(4.spacing))

            EsLargePrimaryButton(
                onClick = {
                    if (state.addAddress) {
                        if (addressValidation.addressForm.validate()) {
                            if (addressState.address.value.id.isEmpty()) {
                                onAddressEvent(AddressEvent.AddAddress)
                            } else {
                                onAddressEvent(AddressEvent.EditAddress)
                            }
                        }
                    } else {
                        onEvent(OrganizationDetailsEvent.EditOrganization)
                    }
                },
                loading = state.editOrganization.isLoading ||
                    addressState.addEditAddress.isLoading,
                text = "Save",
            )
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    horizontal = 2.5f.spacing,
                    vertical = 2.spacing,
                ),
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(1.5.spacing),
                modifier = Modifier
                    .clip(RoundedCornerShape(2.spacing))
                    .clickable(
                        enabled = addressState.address.value.id.isEmpty(),
                    ) {
                        onEvent(OrganizationDetailsEvent.AddRemoveAddress())
                    }
                    .padding(1.spacing),
            ) {
                Text(
                    text = if (state.addAddress)
                        if (addressState.address.value.id.isNotEmpty())
                            "Edit address" else
                            "Remove address" else
                        "Add address",
                    textAlign = TextAlign.Start,
                    style = MaterialTheme.esTypography.p1Medium16,
                    color = if (state.addAddress)
                        if (addressState.address.value.id.isNotEmpty())
                            MaterialTheme.esColorScheme.textAccent else
                            MaterialTheme.esColorScheme.textError else
                        MaterialTheme.esColorScheme.textAccent,
                )

                if (addressState.address.value.id.isEmpty())
                    EsLargeIcon(
                        res = if (state.addAddress)
                            EsIcons.ArrowDown else
                            EsIcons.Add,
                        tint = if (state.addAddress)
                            MaterialTheme.esColorScheme.iconError else
                            MaterialTheme.esColorScheme.iconAccent,
                    )
            }

            if (state.addAddress)
                AddressScreen(
                    state = addressState,
                    onEvent = onAddressEvent,
                    validation = addressValidation,
                    innerScreen = true,
                )
        }
    }
}